import React, { useEffect, useState } from "react";
import api, { CLP } from "@/lib/api";
import { DollarSign, Plus, TrendingUp, TrendingDown } from "lucide-react";

/**
 * Widget de comparativo costo estimado vs real + margen final para un servicio.
 * Props: serviceId, refreshKey (fuerza recarga), onRegisterCost (abre modal para agregar costo).
 * Consume /api/finance/services/{id}/margin.
 */
export default function ServiceMargin({ serviceId, refreshKey = 0, onRegisterCost }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!serviceId) return;
    api.get(`/finance/services/${serviceId}/margin`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [serviceId, refreshKey]);

  if (!data) {
    return (
      <div className="bg-white border border-neutral-200 p-5" data-testid="service-margin-loading">
        <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Cargando margen…</div>
      </div>
    );
  }

  const marginPositive = data.margin >= 0;
  const marginColor = marginPositive ? "#008C3A" : "#B71C1C";
  const overrun = data.real_cost > data.estimated_cost && data.estimated_cost > 0;

  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid="service-margin">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Costos y margen</div>
        <button
          onClick={onRegisterCost}
          data-testid="service-register-cost"
          className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-[#FF5A00] hover:bg-[#FF5A00]/5 border border-[#FF5A00]/30 px-2 py-1 inline-flex items-center gap-1"
        >
          <Plus className="w-2.5 h-2.5" />Registrar costo
        </button>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-neutral-600">Costo estimado</span>
          <span className="font-mono-kpi font-medium" data-testid="margin-estimated">{CLP(data.estimated_cost)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-neutral-600">
            Costo real
            {data.expense_count > 0 && <span className="ml-1.5 text-[10px] text-neutral-400 font-mono-kpi">({data.expense_count})</span>}
          </span>
          <span className={`font-mono-kpi font-medium ${overrun ? "text-[#B71C1C]" : ""}`} data-testid="margin-real">{CLP(data.real_cost)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
          <span className="text-neutral-600">
            Venta neta
            {data.invoice_count > 0 && <span className="ml-1.5 text-[10px] text-neutral-400 font-mono-kpi">({data.invoice_count})</span>}
          </span>
          <span className="font-mono-kpi font-medium" data-testid="margin-sale">{CLP(data.sale_net)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-neutral-200">
          <span className="font-bold flex items-center gap-1">
            {marginPositive ? <TrendingUp className="w-3.5 h-3.5" style={{ color: marginColor }} /> : <TrendingDown className="w-3.5 h-3.5" style={{ color: marginColor }} />}
            Margen final
          </span>
          <div className="text-right">
            <div className="font-mono-kpi font-bold text-base" style={{ color: marginColor }} data-testid="margin-value">{CLP(data.margin)}</div>
            {data.margin_pct != null && (
              <div className="font-mono-kpi text-[10px] uppercase tracking-wider" style={{ color: marginColor }} data-testid="margin-pct">{data.margin_pct}%</div>
            )}
          </div>
        </div>
      </div>

      {overrun && (
        <div className="mt-3 border border-[#B71C1C]/30 bg-[#B71C1C]/5 px-2 py-1.5 text-[11px] text-[#B71C1C]" data-testid="margin-overrun">
          ⚠ El costo real supera al estimado
        </div>
      )}
      {data.sale_net === 0 && data.real_cost > 0 && (
        <div className="mt-3 border border-[#B28C00]/30 bg-[#FFC107]/10 px-2 py-1.5 text-[11px] text-[#B28C00]" data-testid="margin-no-invoice">
          Aún sin factura vinculada
        </div>
      )}
    </div>
  );
}
